import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Icon, Input, Modal, Skeleton } from '@vault/ui';
import type { Collection } from '@vault/shared';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { useT } from '../i18n/index.js';
import {
  ColorPicker,
  ProUpsellModal,
  Swatch,
  errMessage,
  favVar,
  isApiStatus,
  isRecent,
  useToasts,
} from './_vault/shared.js';

export function CollectionsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { isPro } = useAuth();
  const toasts = useToasts();
  const { t } = useT();

  const query = useQuery({ queryKey: ['collections'], queryFn: () => api.collections.list() });

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(favVar(0));
  const [upsell, setUpsell] = useState(false);

  function openCreate() {
    setName('');
    setColor(favVar(Math.floor(Math.random() * 8)));
    setCreateOpen(true);
  }

  const createMut = useMutation({
    mutationFn: (v: { name: string; color: string }) => api.collections.create(v.name, v.color),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['collections'] });
      setCreateOpen(false);
      setName('');
      toasts.show({ tone: 'success', title: 'Collection created', description: `“${res.collection.name}” is ready.` });
      navigate(`/collections/${res.collection.id}`);
    },
    onError: (err) => {
      if (isApiStatus(err, 402)) {
        setCreateOpen(false);
        setUpsell(true);
        return;
      }
      toasts.show({ tone: 'danger', title: 'Could not create collection', description: errMessage(err) });
    },
  });

  function submitCreate() {
    const trimmed = name.trim();
    if (!trimmed || createMut.isPending) return;
    createMut.mutate({ name: trimmed, color });
  }

  const collections: Collection[] = query.data?.collections ?? [];

  return (
    <div className="colg">
      <style>{CSS}</style>

      <div className="colg-top">
        <h1 className="colg-h1">{t('collections.title')}</h1>
        <span className="colg-sub">{t('collections.subtitle')}</span>
        <span className="colg-new-btn">
          <Button variant="ghost" size="sm" icon={<Icon name="plus" size={12} />} onClick={openCreate}>
            {t('collections.new')}
          </Button>
        </span>
      </div>

      {!isPro && (
        <div className="colg-banner">
          <Icon name="sparkle" size={14} color="var(--accent-ink)" />
          <span>
            <b style={{ color: 'var(--text-1)' }}>{t('collections.banner.title')}</b> {t('collections.banner.body')}
          </span>
        </div>
      )}

      {query.isLoading && (
        <div className="colg-grid">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={118} radius={14} />
          ))}
        </div>
      )}

      {query.isError && (
        <div className="colg-error">
          <span style={{ fontSize: 13, fontWeight: 600 }}>{t('collections.error.title')}</span>
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{errMessage(query.error)}</span>
          <Button variant="ghost" size="sm" onClick={() => query.refetch()}>{t('common.tryAgain')}</Button>
        </div>
      )}

      {!query.isLoading && !query.isError && collections.length === 0 && (
        <div style={{ maxWidth: 460, margin: '40px auto' }}>
          <EmptyState
            dashed
            icon={<Icon name="folder" size={18} color="var(--text-3)" />}
            title={t('collections.empty.title')}
            description={t('collections.empty.body')}
            action={
              <div style={{ marginTop: 4 }}>
                <Button variant="primary" size="sm" icon={<Icon name="plus" size={12} color="#fff" />} onClick={openCreate}>
                  {t('collections.new')}
                </Button>
              </div>
            }
          />
        </div>
      )}

      {!query.isLoading && !query.isError && collections.length > 0 && (
        <div className="colg-grid">
          {collections.map((c) => (
            <button key={c.id} type="button" className="colg-card" onClick={() => navigate(`/collections/${c.id}`)}>
              <div className="colg-card-top">
                <Swatch seed={c.id} color={c.color} size={15} />
                {c.kind === 'ai' ? (
                  <Badge tone="warn" pill={false}>
                    <Icon name="sparkle" size={9} color="var(--amber-ink)" /> Auto
                  </Badge>
                ) : (
                  <Badge tone="neutral" pill={false}>Manual</Badge>
                )}
              </div>
              <span className="colg-name">{c.name}</span>
              <span className="colg-meta">
                <span>{c.itemCount} TAB{c.itemCount === 1 ? '' : 'S'}</span>
                <span>·</span>
                {isRecent(c.updatedAt) ? <span style={{ color: 'var(--success)' }}>ACTIVE</span> : <span>QUIET</span>}
              </span>
            </button>
          ))}

          <button type="button" className="colg-new" onClick={openCreate}>
            <Icon name="plus" size={16} color="var(--text-3)" />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>New collection</span>
          </button>
        </div>
      )}

      <Modal
        open={createOpen}
        width={440}
        title="New collection"
        subtitle="Group saved tabs by topic. You can rename or recolour it anytime."
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={createMut.isPending} disabled={!name.trim()} onClick={submitCreate}>
              Create collection
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input label="Name" placeholder="e.g. Brazil docs" value={name} onChange={(e) => setName(e.target.value)} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Colour</span>
            <ColorPicker value={color} onChange={setColor} />
          </div>
        </div>
      </Modal>

      <ProUpsellModal
        open={upsell}
        onClose={() => setUpsell(false)}
        title="AI collections need Pro"
        body="Pro sorts your whole vault into topic collections automatically and keeps them fresh. Manual collections stay free — upgrade to let AI do the sorting."
      />
      {toasts.node}
    </div>
  );
}

const CSS = `
.colg { padding: 24px 28px; max-width: 1000px; margin: 0 auto; display: flex; flex-direction: column; gap: 16px; }
.colg-top { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.colg-h1 { font-family: var(--font-display-cond); font-size: 22px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase; margin: 0; }
.colg-sub { font-size: 12.5px; color: var(--text-2); }
.colg-new-btn { margin-left: auto; }
.colg-banner { display: flex; align-items: center; gap: 10px; background: var(--accent-tint); border: 1px solid var(--line-accent); border-radius: 12px; padding: 11px 14px; font-size: 12.5px; line-height: 1.5; color: var(--text-2); }
.colg-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; align-content: start; }
.colg-card { font-family: inherit; color: var(--text-1); background: var(--surface-1); border: 1px solid var(--line); border-radius: 14px; box-shadow: var(--shadow-card); padding: 16px; display: flex; flex-direction: column; gap: 12px; cursor: pointer; text-align: left; }
.colg-card:hover { border-color: var(--line-strong); }
.colg-card-top { display: flex; align-items: flex-start; justify-content: space-between; }
.colg-name { font-family: var(--font-display); font-size: 16px; font-weight: 600; letter-spacing: -.01em; }
.colg-meta { display: flex; align-items: center; gap: 8px; font-family: var(--font-mono); font-size: 10px; color: var(--text-3); }
.colg-new { font-family: inherit; color: var(--text-1); border: 1px dashed var(--line-strong); border-radius: 14px; padding: 16px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; cursor: pointer; min-height: 118px; background: transparent; }
.colg-new:hover { background: var(--surface-1); }
.colg-error { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; background: var(--surface-1); border: 1px solid var(--line); border-radius: 14px; padding: 20px; }
@media (max-width: 560px) {
  .colg { padding: 16px; }
  .colg-grid { grid-template-columns: 1fr; }
  .colg-new-btn { margin-left: 0; }
}
`;
